import axios from "axios";
import { loadStripe, Stripe as StripeClient } from "@stripe/stripe-js";
import { getBaseApiUrl, getPaymentPublicKey } from "./EnvironmentManager";
import { LineItem, Payment } from "./OrderManager";
import { useSessionStorage } from "../hooks/useSessionStorage";

const getPaymentApiUrl = () => `${getBaseApiUrl()}/payment/stripe`;

// Public key only (safe for browser)
const stripePromise = loadStripe(getPaymentPublicKey());

const readSessionIdFromUrl = (): string | null => {
  // Stripe uses ?session_id=cs_...
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("session_id");
  } catch {
    return null;
  }
};

const normalizeError = (e: any, fallback = "Payment error") =>
  e?.response?.data?.message ||
  e?.message ||
  e?.toString?.() ||
  fallback;

const usePaymentApi = () => {
  // Store sessionId and normalized payment details in session storage (typed)
  const [paymentSessionId, setPaymentSessionId] = useSessionStorage<string | null>(
    "paymentSessionId",
    null
  );
  const [payment, setPayment] = useSessionStorage<Payment | null>(
    "payment",
    null
  );

  // If you need auth headers, add them here:
  // const { getAccessTokenSilently } = useAuth0();
  const authHeaders = async (): Promise<Record<string, string>> => {
    // const token = await getAccessTokenSilently();
    // return { Authorization: `Bearer ${token}` };
    return {};
  };

  const getStripe = async (): Promise<StripeClient> => {
    const stripe = await stripePromise;
    if (!stripe) throw new Error("Stripe failed to load.");
    return stripe;
  };

  /**
   * Create Checkout Session (server), persist session id, and return normalized Payment.
   * Does NOT redirect; use startCheckout for redirect flow.
   */
  const createPaymentSession = async (lineItems: LineItem[]): Promise<Payment> => {
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(await authHeaders()),
      };
      const resp = await axios.post(getPaymentApiUrl(), { lineItems }, { headers });

      if (resp.status !== 200 || !resp.data?.data) {
        throw new Error("Unexpected response from the server.");
      }

      return resp.data.data;
      /*
            const data = resp.data.data as {
              sessionId: string; // cs_*
              // any additional fields your backend returns
              amountTotal?: number;
              currency?: string;
            };
      
            // Persist session id
            setPaymentSessionId(data.sessionId || null);
      
            // Initialize a minimal payment object (enriched later by getPaymentSession)
            const minimal: Payment = {
              vendorName: "stripe",
              paymentSessionId: data.sessionId,
              amountTotal: data.amountTotal,
              currency: data.currency?.toUpperCase(),
              status: "processing",
            };
            setPayment(minimal);
      
            return minimal;*/
    } catch (e: any) {
      console.error("Failed to create Stripe session:", e);
      throw new Error(normalizeError(e, "Failed to create a Stripe session."));
    }
  };

  /**
   * Convenience: create a session and redirect to Stripe Checkout.
   * success_url/cancel_url should be configured on the server.
   */
  const startCheckout = async (lineItems: LineItem[]) => {
    const stripe = await getStripe();
    const sess = await createPaymentSession(lineItems);
    if (!sess.paymentSessionId) throw new Error("Missing Stripe session id.");

    const { error } = await stripe.redirectToCheckout({ sessionId: sess.paymentSessionId });
    if (error) throw new Error(error.message);
  };

  /**
   * Fetch raw session (or payment) from your backend by session id (enriched)
   * This endpoint should call Stripe with expands and normalize to Payment
   */
  const getPaymentSession = async (sessionId?: string): Promise<Payment> => {
    const id = sessionId || readSessionIdFromUrl() || paymentSessionId || undefined;
    if (!id) throw new Error("Stripe session id not found.");

    try {
      const headers = {
        "Content-Type": "application/json",
        ...(await authHeaders()),
      };
      const resp = await axios.get(`${getPaymentApiUrl()}/${id}`, { headers });
      if (resp.status !== 200 || !resp.data?.data) {
        throw new Error("Unexpected response fetching payment details.");
      }

      // Expect backend to already normalize fields to Payment shape
      const details = resp.data.data as Payment;

      console.log("Fetched payment details:", details);
      // Merge with existing (keep any frontend-known data)
      const merged: Payment = {
        ...(payment || {}),
        ...details,
        paymentSessionId: details.paymentSessionId || id,
        vendorName: details.vendorName || "stripe",
      };

      setPaymentSessionId(merged.paymentSessionId || null);
      setPayment(merged);

      return merged;
    } catch (e: any) {
      console.error("Error fetching payment details:", e);
      throw new Error(normalizeError(e, "Error fetching payment details."));
    }
  };

  /**
   * Lightweight status poller (e.g., on the success page).
   * Resolves early if a terminal state is reached.
   */
  const pollPaymentUntil = async (opts?: {
    sessionId?: string;
    maxAttempts?: number; // default 8
    intervalMs?: number;  // default 1000
    isDone?: (p?: Payment | null) => boolean;
  }): Promise<Payment> => {
    const {
      sessionId,
      maxAttempts = 8,
      intervalMs = 1000,
      isDone = (p: Payment) => {
        const s = (p?.status || "").toLowerCase();
        return ["succeeded", "paid"].includes(s);
      },
    } = opts || {};

    let attempt = 0;
    let last: Payment = payment || ({} as Payment);

    while (attempt < maxAttempts) {
      attempt += 1;
      last = await getPaymentSession(sessionId);
      if (isDone(last)) return last;
      await new Promise((r) => setTimeout(r, intervalMs));
    }

    return last;
  };

  /**
   * Get the persisted minimal payment (may not be enriched).
   */
  const getPayment = async (): Promise<Payment> => {
    // Ensure compatibility with your OrderManager types
    // This returns something you can pass to createOrder/submitOrder.
    const p = payment;
    return {
      vendorName: (p?.vendorName as any) || "stripe",
      paymentSessionId: p?.paymentSessionId,
      status: p?.status,
      paymentType: p?.paymentType,
      amountTotal: p?.amountTotal,
      metadata: {
        paymentIntentId: p?.paymentIntentId,
      },
    };
  };

  /**
   * Clear both stored payment details and session id.
   */
  const clearPaymentAll = async () => {
    setPayment(null);
    setPaymentSessionId(null);
  };

  return {
    // Stripe
    getStripe,

    // Create / redirect
    createPaymentSession,
    startCheckout,

    // Details & polling
    getPaymentSession,
    pollPaymentUntil,

    // Local state accessors
    getPayment,
    payment,
    paymentSessionId,
    setPayment,               // in case you need to seed it manually
    setPaymentSessionId,

    // Clear
    clearPayment: clearPaymentAll,
  };
};

export default usePaymentApi;
